import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import {
    GetDashboardData200Response,
    UsersService,
} from '@viewbid/ts-angular-client';
import { VbSidebarGroup, VbSidebarTab } from '@viewbid/ui';
import { BiddingUtils, InstructionData } from '@/vb-utils/bidding.util';
import { FeatureFlagUtil } from 'src/app/core/utils/feature-flag.util';
import { FeatureFlag } from 'src/app/core/constants/feature-flag.enum';

/**
 * Profile shell — redesigned sidebar layout (profiles redesign, step I1).
 * Left: @viewbid/ui wide sidebar rail (ACCOUNT / ACTIVITY / SETTINGS groups);
 * right: the child-route outlet. The rail flattens itself into a horizontal
 * tab strip at ≤860px, so the old mobile accordion (and its
 * DeviceCheckService dependency) is gone — child routes render identically
 * in both modes.
 */
@Component({
    selector: 'vb-user-profile-page',
    templateUrl: './user-profile-page.component.html',
    styleUrls: ['./user-profile-page.component.scss'],
})
export class UserProfilePageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    instruction?: InstructionData;

    /** Active sidebar tab — tab ids double as the child route paths. */
    activeId: string = '';

    groups: VbSidebarGroup[] = this.buildGroups();

    /**
     * Child routes that are sub-flows of a tab (forms / detail views) keep
     * their parent tab highlighted while active.
     */
    private static readonly PARENT_TAB: Record<string, string> = {
        'user-addresses': 'my-addresses',
        'add-payment-information': 'payment-information',
        'view-invoice': 'my-invoices',
        'invoice-payments': 'my-invoices',
        'my-notifications-reminders': 'notifications',
    };

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private usersService: UsersService,
        private readonly biddingUtils: BiddingUtils,
        private readonly featureFlagUtil: FeatureFlagUtil,
    ) {}

    ngOnInit(): void {
        // Initial state for direct loads; the NavigationEnd stream keeps both
        // the active tab and the instruction overlay in sync afterwards.
        this.syncActiveTab();
        this.loadInstructionForCurrentUrl();

        this.router.events
            .pipe(
                filter(
                    (event: any): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.syncActiveTab();
                this.loadInstructionForCurrentUrl();
            });

        this.loadBadgeCounts();
    }

    onTab(id: string): void {
        this.router.navigate([id], { relativeTo: this.route });
    }

    /** Derive the active tab id from the first child route's leading segment. */
    private syncActiveTab(): void {
        const segment: string =
            this.route.snapshot.firstChild?.url[0]?.path ?? '';
        this.activeId = UserProfilePageComponent.PARENT_TAB[segment] ?? segment;
    }

    private loadInstructionForCurrentUrl(): void {
        this.biddingUtils
            .getInstructionForCurrentPage$(window.location.href)
            .pipe(takeUntil(this.destroy$))
            .subscribe((instruction: InstructionData | null) => {
                this.instruction = instruction ?? undefined;
            });
    }

    /**
     * One-shot fetch of the sidebar badge counts (invoices needing action).
     * Badges are garnish: on error — or when a count is 0/absent — the pills
     * simply stay off. The Notifications tab deliberately carries no count:
     * it's a settings page (preferences), not an inbox, so an unread tally
     * there was misleading.
     */
    private loadBadgeCounts(): void {
        this.usersService
            .getDashboardData()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ data }: GetDashboardData200Response) => {
                    this.groups = this.buildGroups(data.numInvoices);
                },
                error: () => {
                    /* leave badge counts off */
                },
            });
    }

    private buildGroups(invoiceCount?: number): VbSidebarGroup[] {
        return [
            {
                label: 'Account',
                tabs: [
                    { id: 'profile', label: 'Overview', icon: 'user' },
                    {
                        id: 'user-information',
                        label: 'Information',
                        icon: 'info',
                    },
                    { id: 'my-addresses', label: 'Address', icon: 'mapPin' },
                    {
                        id: 'payment-information',
                        label: 'Payment',
                        icon: 'card',
                    },
                ],
            },
            {
                label: 'Activity',
                tabs: [
                    { id: 'my-bidding', label: 'My bidding', icon: 'history' },
                    // Gated behind FeatureFlag.MyAppointments — dev instance
                    // only for now (see environment.dev.ts).
                    ...(this.featureFlagUtil.isFeatureEnabled(
                        FeatureFlag.MyAppointments,
                    )
                        ? [
                              {
                                  id: 'my-appointments',
                                  label: 'My appointments',
                                  icon: 'clock',
                              } as VbSidebarTab,
                          ]
                        : []),
                    {
                        id: 'my-invoices',
                        label: 'Invoices',
                        icon: 'invoice',
                        count: invoiceCount || undefined,
                    },
                    { id: 'messages', label: 'Messages', icon: 'mail' },
                    { id: 'my-offers', label: 'My offers', icon: 'tag' },
                ],
            },
            {
                label: 'Settings',
                tabs: [
                    {
                        id: 'notifications',
                        label: 'Notifications',
                        icon: 'bell',
                    },
                    {
                        id: 'newsletter',
                        label: 'Newsletter',
                        icon: 'note',
                    },
                ],
            },
        ];
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
